import React, { useCallback, useEffect, useMemo } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { Label } from "@/components/ui/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { type TagListItem } from "@/app/types/tags";
import { type Status } from "@/app/shared_enums/enums";
import { type Event } from "@/app/types/event";
import { Button } from "@/components/ui/Button";
import { Trash2 } from "lucide-react";
import { PathDropdown } from "@/components/ui/PathDropdown";
import { createTagTreeFromTagNodes } from "@/utils/Helper";
import { CounterFormType } from "./CounterForm";

export const EventAssignment = ({
  eventId,
  setEventId,
  events,
  form,
  tags,
  onDelete
}: {
  eventId: number | undefined;
  setEventId: (eventId: number) => void;
  events: Event[];
  tags: TagListItem[];
  form: UseFormReturn<CounterFormType, any, undefined>;
  onDelete: () => void;
}) => {
  const filteredTags = React.useMemo(() => {
    if (!eventId) return tags;
    // Show tags for the selected event OR global tags (event_id is null)
    return tags.filter(
      (tag) =>
        (tag.event_id !== null && Number(tag.event_id) === eventId) ||
        tag.event_id === null,
    );
  }, [tags, eventId]);
  const groupedTags = useMemo(
    () => (filteredTags.length > 0 ? createTagTreeFromTagNodes(filteredTags) : []),
    [filteredTags]
  );
  const { control, formState: {errors} } = form;
  const isSameEvent = useCallback(
    (path: number[], otherEventId: number | null = null) => 
      tags.find(t => 
        path.length > 0 && t.tag_id === path[path.length - 1]
      )?.event_id === (otherEventId ?? eventId), 
    [tags, eventId]
  )

  return (
    <div className="relative space-y-2 p-3 bg-gray-100/40 rounded border group">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={onDelete}
      >
        <Trash2 className="h-4 w-4 text-red-500" />
      </Button>

      <div className="relative w-full ">
        <Label>Event</Label>
        <Select
          onValueChange={(val) => {
            const prevEventId = eventId;
            setEventId(Number(val));
            if (!prevEventId) return;
            form.setValue("path", 
              form.getValues('path').filter(tid => isSameEvent(tid, prevEventId))
            ); 
          }}
          value={eventId ? eventId.toString() : undefined}
        >
          <SelectTrigger
            className="w-full bg-white"
            labelClassname="bg-white bg-[length:50%_100%] bg-no-repeat"
          >
            <SelectValue placeholder="Select Event" />
          </SelectTrigger>
          <SelectContent>
            {events.map((event) => (
              <SelectItem
                key={event.event_id}
                value={event.event_id.toString()}
              >
                {event.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="relative w-full ">
        <Label>Services</Label>
        <Controller
          control={control}
          name="path"
          render={({ field }) => {
            const eventTags = field.value.filter(path => isSameEvent(path));
            return (
              <PathDropdown
                key={eventId}
                value={eventTags}
                onChange={(val) => field.onChange([...new Set([...field.value.filter(tid => !isSameEvent(tid)), ...val])])}
                groups={groupedTags}
                placeholder="Select Services"
                searchPlaceholder="Search Services..."
                noResultsText="No Services found"
                inputClassname="border border-checkboxborder rounded-md bg-white"
                placeholderClassname="text-gray-800 text-sm"
                multiple
              />
            )
          }} 
        />
      </div>
    </div>
  )
}
